import React, { SyntheticEvent, useMemo, useRef } from 'react'

import { IQuestionViewProps, AnswerID } from '../types'
import { MaxdiffAnswer, MaxdiffAnswers, MaxdiffQuestionFrameModelTypes } from './types'

import ImageAsset from '../../modules/images/ImageAsset'
import { getMediumAssetPath } from '@aytm/lib/common/assets'
import TextWithOptions from '@aytm/app/swidget/base/modules/preview/components/TextWithOptions'
import { hapticEffects } from '../../lib/hapticEffects'

import useSurveyLocale from '../../modules/locale/useSurveyLocale'
import useTimelineFrame from '../../modules/timeline/useTimelineFrame'
import useViewport from '../../modules/viewport/useViewport'
import useSession from '@aytm/app/swidget/base/session/useSession'
import usePreviewOptions from '@aytm/app/swidget/base/modules/preview/usePreviewOptions'
import useInterfaceLocale from '../../modules/locale/useInterfaceLocale'

import style from './style.module.scss'
import commonStyle from '../_common/style.module.scss'

const hasImage = (answer: MaxdiffAnswer) =>
  answer.imageName || (answer.image?.original_url && !answer.image?.original_url.includes('no_image'))

const View = ({
  schema,
  frame,
  response,
  timeline,
  showValidation,
  onUpdateResponse,
  responseValidation
}: IQuestionViewProps<MaxdiffQuestionFrameModelTypes>) => {
  const { t } = useSurveyLocale()
  const frameInteractionStart = useTimelineFrame(timeline, frame)
  const viewport = useViewport()
  const mobile = viewport.width < 486
  const session = useSession()
  const previewOptions = usePreviewOptions()
  const { localeOptions } = useInterfaceLocale()
  const fontHeight = 13
  const iClicked = useRef(false)

  const step = useMemo(
    () => (!response.best && !response.worst ? 1 : response.worst ? 3 : 2),
    [response.worst, response.best]
  )

  const onClick = (answerIndex: AnswerID, event: SyntheticEvent) => {
    const el = event.target as Element
    if ('A' === el.tagName) return
    if (iClicked.current) {
      iClicked.current = false
      return
    }
    if (!response.best) {
      onUpdateResponse({ best: answerIndex })
      hapticEffects.selection()
      return
    }
    if (response.best && response.worst && answerIndex == response.worst) {
      onUpdateResponse({ best: response.best })
      return
    }
    if (response.best && !response.worst && answerIndex != response.best) {
      response.worst = answerIndex
      hapticEffects.selection()
      onUpdateResponse({ best: response.best, worst: answerIndex })
    } else onUpdateResponse({})
  }

  const onImageClick = (event: SyntheticEvent) => {
    const el = event.target as Element
    if (
      schema.best_worst &&
      'I' != el.tagName &&
      !el.classList.contains('p-image-mask') &&
      !el.classList.contains('p-image-preview') &&
      !el.classList.contains('p-image-preview-indicator') &&
      !el.classList.contains('p-image-action')
    )
      return
    iClicked.current = true
    setTimeout(() => {
      iClicked.current = false
    }, 100)
  }

  const resizeText = (
    text: string,
    width: number,
    height: number,
    options: { minFontSize: number; widthOffset: number; heightOffset: number }
  ) => {
    const h = document.querySelector('body')
    const d = document.createElement('div')
    const s = document.createElement('span')
    let it: number = Math.min(height, 100)
    d.style.display = 'block'
    d.style.width = `${width - 20}px`
    d.style.height = `${height - 20}px`
    d.style.fontSize = `${it}px`
    d.style.whiteSpace = 'normal'
    d.style.lineHeight = `${it}px`
    // eslint-disable-next-line unicorn/prefer-dom-node-append
    h.appendChild(d)
    s.style.display = 'inline'
    s.style.fontSize = `${it}px`
    s.style.fontFamily = 'Ubuntu, Calibri, Segoe, Helvetica, Arial'
    s.style.lineHeight = `${it}px`
    s.innerHTML = text
    // eslint-disable-next-line unicorn/prefer-dom-node-append
    d.appendChild(s)

    let sw = s.offsetWidth
    let sh = s.offsetHeight

    const widthOffset = options && options.widthOffset ? options.widthOffset : 25
    const heightOffset = options && options.heightOffset ? options.heightOffset : viewport.mobile ? 10 : 25
    const minFontSize = options && options.minFontSize ? options.minFontSize : 15

    while ((sw > width - widthOffset || sh > height - heightOffset) && it > minFontSize) {
      it -= Math.ceil(sw / (width - 5))
      d.style.fontSize = `${it}px`
      d.style.lineHeight = `${it}px`
      s.style.fontSize = `${it}px`
      s.style.lineHeight = `${it}px`
      sw = s.offsetWidth
      sh = s.offsetHeight
    }

    // eslint-disable-next-line unicorn/prefer-dom-node-remove
    h.removeChild(d)
    return {
      fontSize: `${it}px`,
      lineHeight: `${it}px`
    }
  }

  const getLabel = (answer: MaxdiffAnswer) => {
    if (answer.id === response?.best) {
      return t<(value: string) => string>('questions', 'md', 'firstSelected')(answer.text)
    }
    if (answer.id === response?.worst) {
      return t<(value: string) => string>('questions', 'md', 'lastSelected')(answer.text)
    }
    if (!response.best) {
      return t<(value: string) => string>('questions', 'md', 'selectFirst')(answer.text)
    }
    if (!response.worst) {
      return t<(value: string) => string>('questions', 'md', 'selectLast')(answer.text)
    }
    return answer.text
  }

  const getMinimumResizedStyle = (items: MaxdiffAnswers, width: number, height: number) =>
    items.reduce(
      (res, item) => {
        const fontStyleItem = resizeText(item.text, width, height, {
          minFontSize: 1,
          widthOffset: viewport.mobile ? 5 : 25,
          heightOffset: viewport.mobile ? 35 : 40
        })
        return !res.fontSize ||
          parseInt(fontStyleItem.fontSize.split('px')[0], 10) < parseInt(res.fontSize.split('px')[0], 10)
          ? fontStyleItem
          : res
      },
      { fontSize: '' }
    )

  const textStyle = !schema.image_grid_columns
    ? {}
    : getMinimumResizedStyle(
        schema.answers.filter(answer => !hasImage(answer)),
        viewport.mobile ? viewport.width * 0.37 : 250,
        viewport.mobile ? viewport.width * 0.37 : 250
      )

  const hasSmallImages = schema.answers.some(answer => answer.image && !answer.image.expand)

  const renderBestWorst = (answer: MaxdiffAnswer) => {
    const bigSrc = answer.imageName ? `${session.action.aytm_domain}/images/widget/md/${answer.imageName}.png` : null
    return (
      <div
        className={`${style.container} ${response.best && answer.id == response.best ? style.best : ' '} ${
          response.worst && answer.id === response.worst ? style.worst : ' '
        } ${viewport.mobile ? commonStyle.mobileView + ' ' + style.mobile : ''}`}
        key={answer.id}
      >
        {answer.image && answer.image?.expand && !bigSrc && (
          <div className={style.bigImageContainer} onClick={onImageClick}>
            <div>
              <ImageAsset
                asset={answer.image}
                className={style.bigImage}
                width={viewport.mobile ? viewport.width : (viewport.widgetWidth as number)}
                // preview={false}
                size="medium"
                onFullImageLoaded={frameInteractionStart}
                previewLabelTemplate={() => (
                  <TextWithOptions
                    text={answer.text}
                    options={previewOptions}
                    rules={session.rules?.sub?.[schema.id]?.[answer.id]}
                  />
                )}
              />
            </div>
          </div>
        )}
        <div className={style.textContainer} data-testid="SW-MDBW-Text">
          {answer.image && !answer.image?.expand && !bigSrc && (
            <div onClick={onImageClick} data-testid="SW-MDBW-Thumbnail-Image">
              <ImageAsset
                asset={answer.image}
                className={style.image}
                width={66}
                height={66}
                size="small"
                position={localeOptions.rtl ? 'right' : 'left'}
                previewLabelTemplate={() => (
                  <TextWithOptions
                    text={answer.text}
                    options={previewOptions}
                    rules={session.rules?.sub?.[schema.id]?.[answer.id]}
                  />
                )}
              />
            </div>
          )}
          {(!answer.image || answer.image.expand) && hasSmallImages && (
            <ImageAsset asset={null} className={style.image} width={66} height={66} size="small" />
          )}
          {(previewOptions.showHidden || !session.rules?.hide_text?.[answer.id]?.condition) && !bigSrc && (
            <span className={style.text}>
              <TextWithOptions
                text={answer.text}
                options={previewOptions}
                rules={session.rules?.sub?.[schema.id]?.[answer.id]}
              />
            </span>
          )}
          {bigSrc && (
            <div
              data-testid="SW-MDBW-BestWorst-Image"
              className={style.numberImageContainer}
              role="img"
              aria-label={answer.image?.alt_text || t('questions', 'img', 'noAlt')}
              style={{
                backgroundImage: `url(${bigSrc})`
              }}
            />
          )}
          <div
            className={`${style.sign} ${answer.image && !answer.image?.expand ? style.withImage : ' '}`}
            data-testid="SW-MDBW-Sign"
          >
            <i className={`${style.up} icon-aytmfd-thumbsUp`} />
            <i className={`${style.down} icon-aytmfd-thumbsDown`} />
          </div>
        </div>
      </div>
    )
  }
  const renderGrid = (answer: MaxdiffAnswer) => {
    const bigSrc = answer.imageName
      ? `${session.action.aytm_domain}/images/widget/md/${answer.imageName}.png`
      : getMediumAssetPath(answer.image)
    const showImage = hasImage(answer)

    return (
      <>
        <div
          data-testid="SW-MDBW-Grid-Image"
          className={style.imageContainer}
          role={showImage ? 'img' : undefined}
          aria-label={showImage ? answer.image?.alt_text || t('questions', 'img', 'noAlt') : undefined}
          style={{
            backgroundImage: showImage ? `url(${bigSrc})` : 'none'
          }}
        />
        <div className={style.imageBorder} />
        {!showImage && (
          <span className={`${style.imageText} ${mobile ? style.mobile : ''}`} style={textStyle}>
            {(previewOptions.showHidden || !session.rules?.hide_text?.[answer.id]?.condition) && (
              <TextWithOptions
                text={answer.text}
                options={previewOptions}
                rules={session.rules?.sub?.[schema.id]?.[answer.id]}
              />
            )}
          </span>
        )}
        <div
          className={`${style.sign} ${answer.image && !answer.image?.expand ? style.withImage : ' '}`}
          data-testid="SW-MDBW-Sign"
        >
          <i className={`${style.up} icon-aytmfd-thumbsUp`} />
          <i className={`${style.down} icon-aytmfd-thumbsDown`} />
        </div>
        <div onClick={onImageClick}>
          <ImageAsset
            asset={answer.image}
            size="medium"
            className={style.hiddenAsset}
            noPreloader={true}
            previewLabelTemplate={() => (
              <TextWithOptions
                text={answer.text}
                options={previewOptions}
                rules={session.rules?.sub?.[schema.id]?.[answer.id]}
              />
            )}
          />
        </div>

        {showImage && (previewOptions.showHidden || !session.rules?.hide_text?.[answer.id]?.condition) && (
          <span
            className={style.text}
            data-testid="SW-MDBW-AnswerText"
            style={{
              maxHeight: answer.height * fontHeight || 'inherit'
            }}
          >
            <TextWithOptions
              text={answer.text}
              options={previewOptions}
              rules={session.rules?.sub?.[schema.id]?.[answer.id]}
            />
          </span>
        )}
      </>
    )
  }
  return (
    <fieldset
      className={`${schema.best_worst ? style.bestWorst : style.grid} ${localeOptions.rtl && style.RTL} ${
        style[`stage${step}`]
      }`}
      data-testid="SW-MDBW"
      role="group"
    >
      <legend>
        <div
          role="region"
          aria-live="polite"
          aria-atomic="true"
          data-testid="SW-MDBW-ValidationMessage"
          className={`${commonStyle.validationMessage}${
            showValidation && !responseValidation.valid ? ' ' + commonStyle.error : ''
          }`}
          dangerouslySetInnerHTML={{ __html: t('questions', 'md', `note${step}`) }}
        />
      </legend>
      {schema.answers.map(answer => (
        <button
          role="button"
          className={`${style.container} ${style.up} ${
            response.best && answer.id == response.best ? style.best : ' '
          } ${response.worst && answer.id === response.worst ? style.worst : ' '}`}
          key={answer.id}
          onClick={event => onClick(answer.id, event)}
          data-testid="SW-MDBW-Answer"
          style={{ marginBottom: answer.height * 13 || 0 }}
          aria-label={getLabel(answer)}
        >
          {schema.best_worst ? renderBestWorst(answer) : renderGrid(answer)}
        </button>
      ))}
    </fieldset>
  )
}

export default View
